import { useRef, useState, type DragEvent } from "react";
import { Icon, IconButton } from "@otrack/ui";

export const LOGO_ACCEPT = "image/png,image/jpeg,image/webp";

/**
 * The Logo (or a Client Picture) as one tile. Empty, it is a drop target to click or drop an image onto. With a Logo,
 * hovering (or focusing) it offers Replace over the whole tile and Remove in its corner. It sits
 * on paper white because Invoices always print on light paper.
 */
export function LogoPicker({ logo, busy, noun = "logo", alt = "Your logo", onFile, onRemove }: { logo: string | null; busy: boolean; noun?: string; alt?: string; onFile: (file: File) => void; onRemove: () => void }) {
  const [over, setOver] = useState(false);
  const depth = useRef(0);
  const take = (file: File | undefined) => { if (file && !busy) onFile(file); };

  const drag = {
    onDragEnter: (event: DragEvent) => { event.preventDefault(); depth.current += 1; setOver(true); },
    onDragOver: (event: DragEvent) => { event.preventDefault(); event.dataTransfer.dropEffect = "copy"; },
    onDragLeave: () => { depth.current = Math.max(0, depth.current - 1); if (!depth.current) setOver(false); },
    onDrop: (event: DragEvent) => {
      event.preventDefault();
      depth.current = 0;
      setOver(false);
      take(event.dataTransfer.files[0]);
    }
  };

  return <div className={`ot-logo ${logo ? "has-logo" : ""} ${over ? "is-over" : ""} ${busy ? "is-busy" : ""}`}>
    <label className="ot-logo__tile" {...drag}>
      <input type="file" accept={LOGO_ACCEPT} disabled={busy} aria-label={logo ? `Replace ${noun}` : `Add ${noun}`} onChange={(event) => { take(event.target.files?.[0]); event.target.value = ""; }}/>
      {logo && <img src={logo} alt={alt}/>}
      <span className="ot-logo__veil">
        <Icon name="upload" weight="bold"/>
        <strong>{busy ? "Saving…" : over ? `Drop to use this ${noun}` : logo ? `Replace ${noun}` : `Add ${noun}`}</strong>
        {!logo && !busy && !over && <span>Click, or drop an image</span>}
      </span>
    </label>
    {logo && <IconButton className="ot-logo__remove" label={`Remove ${noun}`} icon="trash" disabled={busy} onClick={onRemove}/>}
  </div>;
}
